import React from "react";
import VideoCard from "../ui/VideoCard";
import {
  Snackbar,
  Alert,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import OcrForm from "../ui/OcrForm";
import FaceVerificationForm from "../ui/FaceVerificationForm";
import { cropImage, resizeImage } from "../../utils/image-util";
import {
  convertAddressOcrData,
  getOcrData,
  ocrBackCardChecking,
  ocrFrontCardChecking,
} from "../../utils/ocr";
import {
  faceVerificationChecking,
  getFaceVerificationData,
} from "../../utils/face-verification";
import { FaceMesh } from "@mediapipe/face_mesh";
import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs-backend-webgl"; // set backend to webgl
import {
  BackCardDialogBody,
  FaceDialogBody,
  FrontCardDialogBody,
} from "../ui/tutorial-popup-body";
import { useStateRef } from "../../utils/custom-react-hook";
import { useOpenCv } from "opencv-react";

// With card detection
export default function HomePage2Core() {
  //---------- Face Detection ----------//
  const [faceDetection, setFaceDetection, faceDetectionRef] = useStateRef(null);
  const [cardDetection, setCardDetection] = useStateRef(null);
  const { loaded } = useOpenCv();

  /*eslint-disable */
  React.useEffect(() => {
    const loadModels = async () => {
      // Load card detection model
      const yolov5 = await tf.loadGraphModel(`/yolov5n_web_model/model.json`);
      // Warmup the card detection before using real data
      const dummyInput = tf.ones(yolov5.inputs[0].shape);
      await yolov5.executeAsync(dummyInput).then((warmupResult) => {
        tf.dispose(warmupResult);
        tf.dispose(dummyInput);
      });
      setCardDetection(yolov5);

      // Load face detection model
      const faceMesh = new FaceMesh({
        locateFile: (file) => {
          return "/face_mesh/" + file;
        },
      });
      faceMesh.setOptions({
        selfieMode: true,
        maxNumFaces: 1,
        refineLandmarks: true,
      });

      let smallImage = new Image();
      smallImage.onload = function () {
        faceMesh.onResults(() => {
          setFaceDetection(faceMesh);
          setLoading(false);
        });
        faceMesh.send({ image: smallImage });
      };
      smallImage.src = `/face.jpg`;
    };

    loadModels();

    // Clean up when component unmount. This is react syntax.
    return () => {
      if (
        faceDetectionRef.current !== null &&
        faceDetectionRef.current !== undefined
      ) {
        faceDetectionRef.current.close();
      }
    };
  }, []);
  /*eslint-enable */

  //---------- Snackbar stuff ----------//
  // The only important thing that you need to know is that you can use handleAlert function to display error message
  // handleAlert can also be passed to other child components
  const [snackPack, setSnackPack] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState(undefined);

  React.useEffect(() => {
    if (snackPack.length && !messageInfo) {
      // Set a new snack when we don't have an active one
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      // Close an active snack when a new one is added
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  const handleAlert = (message, warning) => {
    setSnackPack((prev) => [
      ...prev,
      { message, warning, key: new Date().getTime() },
    ]);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    setMessageInfo(undefined);
  };

  //---------- Main stuff ----------//

  // All images are url base64 data
  const [frontCardImage, setFrontCardImage] = React.useState(null);
  const [backCardImage, setBackCardImage] = React.useState(null);
  const [liveFaceImage, setLiveFaceImage] = React.useState(null);

  // Result data after calling OCR/FaceVerification API
  const [ocrFrontCardResult, setOcrFrontCardResult] = React.useState({});
  const [ocrBackCardResult, setOcrBackCardResult] = React.useState({});
  const [faceIdResult, setFaceIdResult] = React.useState({});

  // Loading state of the app when waiting for response from API
  const [loading, setLoading] = React.useState(true);

  // Feature enable
  const [backCardDisable, setBackCardDisable] = React.useState(true);
  const [liveFaceDisable, setLiveFaceDisable] = React.useState(true);

  const updateFrontCardImage = async (
    data,
    width,
    height,
    cropWidth,
    cropHeight
  ) => {
    setLoading(true);
    let resizedImageData = await cropImage(
      data,
      width,
      height,
      cropWidth,
      cropHeight
    );
    setFrontCardImage(resizedImageData);
    setOcrFrontCardResult({});

    // Resetting data since back card and live face image are based on front card result
    backCardImage && setBackCardImage(null);
    liveFaceImage && setLiveFaceImage(null);
    setOcrBackCardResult({});
    setFaceIdResult({});
    setBackCardDisable(true);
    setLiveFaceDisable(true);
    try {
      let ocrData = await getOcrData(resizedImageData);
      await convertAddressOcrData(ocrData);
      Object.keys(ocrData).forEach((key) => {
        if (
          ocrData[key] === null ||
          ocrData[key] === "N/A" ||
          ocrData[key] === ""
        ) {
          delete ocrData[key];
        }
      });
      console.log("-----Front card-----");
      console.log(ocrData);
      setOcrFrontCardResult(ocrData);
      let frontCardValidation = await ocrFrontCardChecking(ocrData);
      if (!frontCardValidation.success) {
        handleAlert(frontCardValidation.message);
      } else {
        setBackCardDisable(false);
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateBackCardImage = async (
    data,
    width,
    height,
    cropWidth,
    cropHeight
  ) => {
    setLoading(true);
    let resizedImageData = await cropImage(
      data,
      width,
      height,
      cropWidth,
      cropHeight
    );
    setBackCardImage(resizedImageData);
    setOcrBackCardResult({});

    liveFaceImage && setLiveFaceImage(null);
    setFaceIdResult({});
    setLiveFaceDisable(true);

    try {
      let ocrData = await getOcrData(resizedImageData);
      Object.keys(ocrData).forEach((key) => {
        if (
          ocrData[key] === null ||
          ocrData[key] === "N/A" ||
          ocrData[key] === ""
        ) {
          delete ocrData[key];
        }
      });
      console.log("-----Back card-----");
      console.log(ocrData);
      setOcrBackCardResult(ocrData);

      let backCardValidation = await ocrBackCardChecking(
        ocrData,
        ocrFrontCardResult
      );
      if (!backCardValidation.success) {
        handleAlert(backCardValidation.message);
      } else {
        setLiveFaceDisable(false);
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLiveFaceImage = async (data) => {
    setFaceIdResult({});

    setLiveFaceImage(data);
    setLoading(true);

    try {
      let resizedFaceImage = await resizeImage(data);
      let resizedFrontCardImage = await resizeImage(frontCardImage);
      let faceVerificationData = await getFaceVerificationData(
        resizedFrontCardImage,
        resizedFaceImage
      );
      Object.keys(faceVerificationData).forEach((key) => {
        if (
          faceVerificationData[key] === null ||
          faceVerificationData[key] === "N/A" ||
          faceVerificationData[key] === ""
        ) {
          delete faceVerificationData[key];
        }
      });
      console.log("-----Face Verification-----");
      console.log(faceVerificationData);
      setFaceIdResult(faceVerificationData);

      let faceValidation = await faceVerificationChecking(faceVerificationData);
      if (!faceValidation.success) {
        handleAlert(faceValidation.message);
      } else if (faceValidation.message !== "OK.") {
        handleAlert(faceValidation.message, true);
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ pt: 2 }}>
        <Grid item xs={12} lg={6}>
          <VideoCard
            title="1. Ảnh CMT mặt trước"
            popUpTitle="Để CMT mặt trước vào gần máy ảnh"
            imageData={frontCardImage}
            setImageData={updateFrontCardImage}
            handleAlert={handleAlert}
            tutorialTitle="Hướng dẫn chụp mặt trước CMT"
            cardDetection={cardDetection}
            tutorialContent={FrontCardDialogBody}
          />
          <VideoCard
            title="2. Ảnh CMT mặt sau"
            popUpTitle="Để CMT mặt sau vào gần máy ảnh"
            imageData={backCardImage}
            setImageData={updateBackCardImage}
            handleAlert={handleAlert}
            disabled={backCardDisable}
            tutorialTitle="Hướng dẫn chụp mặt sau CMT"
            cardDetection={cardDetection}
            tutorialContent={BackCardDialogBody}
          />
          <VideoCard
            title="3. Ảnh khuôn mặt"
            popUpTitle="Hãy làm theo chỉ dẫn phía dưới"
            imageData={liveFaceImage}
            setImageData={updateLiveFaceImage}
            handleAlert={handleAlert}
            disabled={liveFaceDisable}
            faceDetection={faceDetection}
            tutorialTitle="Hướng dẫn xác thực khuôn mặt"
            tutorialContent={FaceDialogBody}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <OcrForm
            frontCardData={ocrFrontCardResult}
            backCardData={ocrBackCardResult}
          />
          <FaceVerificationForm faceData={faceIdResult} />
        </Grid>
      </Grid>

      <Snackbar
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
      >
        <Alert
          onClose={handleClose}
          severity={
            messageInfo ? (messageInfo.warning ? "warning" : "error") : "error"
          }
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {messageInfo ? messageInfo.message : undefined}
        </Alert>
      </Snackbar>

      <Backdrop open={loading || !loaded}>
        <CircularProgress disableShrink color="info"></CircularProgress>
      </Backdrop>
    </>
  );
}
